import { Button } from '@shared/components/ui/button';
import { faTriangleExclamation, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Order, OrderError, OrderCustomer } from '../../models/Order';
import { SolveErrorsModal, SolveErrorsModalRef } from '../modals';

interface ErrorAlertProps {
  errors?: OrderError[];
  customer?: OrderCustomer;
  orderData?: Order;
  onRefetch?: () => void;
}

export const ErrorAlert: FC<ErrorAlertProps> = ({ errors = [], customer, orderData, onRefetch }) => {
  const { t } = useTranslation(['viewOrdersModule', 'ordersModule']);
  const solveErrorsModalRef = useRef<SolveErrorsModalRef>(null);

  // No mostrar el alert si no hay errores
  if (!errors || errors.length === 0) {
    return null;
  }

  // Determinar el mensaje a mostrar
  const getErrorMessage = () => {
    if (errors.length === 1) {
      // Un solo error: mostrar el texto específico del error
      const errorKey = errors[0].error_key;
      return t(`errors.${errorKey}`, {
        ns: 'ordersModule',
        defaultValue: errorKey,
      });
    } else {
      // Múltiples errores: mostrar cantidad
      return t('alerts.multiple-errors', {
        ns: 'viewOrdersModule',
        count: errors.length,
        defaultValue: `Hay ${errors.length} errores en el pedido para solucionar`,
      });
    }
  };

  const handleSolveClick = () => {
    solveErrorsModalRef.current?.openModal();
  };

  return (
    <>
      <div className="flex items-center justify-between rounded-lg border border-error dark:border-[#4C0707] bg-bg-error dark:bg-[#280B13] p-4">
        <div className="flex items-center gap-3">
          <div>
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#991B1B] dark:bg-[#50171D]">
              <FontAwesomeIcon icon={faTriangleExclamation} className="text-[#BC7679] light:!text-btn-text w-3 h-3" />
            </div>
          </div>
          <span className="text-sm text-error text-[#BC7679] light:text-[#991B1B]">{getErrorMessage()}</span>
        </div>
        <Button variant="primary" size="sm" className="bg-lines light:bg-[#991B1B]" onClick={handleSolveClick}>
          {/* Ícono visible solo en mobile */}
          <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4 md:hidden" />
          {/* Texto visible solo en desktop */}
          <span className="hidden md:inline">{t('alerts.solve-button')}</span>
        </Button>
      </div>

      {/* Modal de solucionar errores */}
      <SolveErrorsModal
        ref={solveErrorsModalRef}
        customer={customer}
        orderData={orderData}
        errors={errors}
        onRefetch={onRefetch}
      />
    </>
  );
};
